import { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@components/date-picker';
import { Input } from '@components/input';
import { InputTime } from '@components/input-time/src/input-time';
import { LabelPrimary } from '@components/label';
import { LabelInfo } from '@components/label/src/label-info';
import { OUTLINE_TAG_SIZE_XS, OUTLINE_TAG_VARIANT_WARNING, OutlineTag } from '@components/outline-tag';
import { SectionDisclosure } from '@components/section-disclosure';
import { Select, SelectOption } from '@components/select';
import { ToggleSection } from '@components/toggle-section';
import {
  TOGGLE_SECTION_BADGE_NEW,
  TOGGLE_SECTION_SIZE_L,
  TOGGLE_SECTION_SIZE_S,
  ToggleSectionSize,
} from '@components/toggle-section/src/toggle-section.type';
import { SM_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';
import { Moment } from 'moment-timezone';

import {
  AdvancedSettingsSectionProps,
  DrinksValue,
  ENTRANCE_TIME_SLOT_MODE_EVENT,
  ENTRANCE_TIME_SLOT_MODE_LIMITED,
  EntranceTimeSlotMode,
  EntranceTimeSlotValue,
  LowStockThresholdValue,
} from './advanced-settings-section.type';

export const ADVANCED_SETTINGS_SECTION_TEST_ID = 'advanced-settings-section';

const tid = (prefix: string, suffix: string) => `${prefix}-${suffix}`;
const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

const FieldError = ({ prefix, testid, message }: { prefix: string; testid: string; message?: string }) =>
  message ? (
    <span data-testid={tid(prefix, `${testid}-error`)} className='text-body-xs text-critical-no-bg'>
      {message}
    </span>
  ) : null;

const DateTimeRow = ({
  prefix,
  testid,
  label,
  date,
  time,
  onDateChange,
  onTimeChange,
  minBookingDate,
  maxBookingDate,
  timezone,
  locale,
  disabled,
  hasDateError,
  hasTimeError,
  dateError,
  timeError,
}: {
  prefix: string;
  testid: string;
  label: string;
  date: Moment | null;
  time: string;
  onDateChange: (next: Moment | null) => void;
  onTimeChange: (next: string) => void;
  minBookingDate?: Moment;
  maxBookingDate?: Moment;
  timezone: string;
  locale: string;
  disabled: boolean;
  hasDateError: boolean;
  hasTimeError: boolean;
  dateError?: string;
  timeError?: string;
}) => (
  <div className='flex flex-col gap-2' data-testid={tid(prefix, testid)}>
    <LabelPrimary marginBottomClassName='' customColors='text-secondary'>
      {label}
    </LabelPrimary>
    <div className='flex gap-3'>
      <div className='flex-1'>
        <DatePicker
          value={date}
          onChange={onDateChange}
          placeholder='-'
          locale={locale}
          timezone={timezone}
          minBookingDate={minBookingDate}
          maxBookingDate={maxBookingDate}
          showClearDate
          hasError={hasDateError}
          disabled={disabled}
          readOnly={disabled}
          data-testid={tid(prefix, `${testid}-date`)}
        />
        <FieldError prefix={prefix} testid={`${testid}-date`} message={dateError} />
      </div>
      <div className='w-24 shrink-0'>
        <InputTime
          value={time}
          onChange={onTimeChange}
          hasError={hasTimeError}
          readOnly={disabled}
          data-testid={tid(prefix, `${testid}-time`)}
        />
        <FieldError prefix={prefix} testid={`${testid}-time`} message={timeError} />
      </div>
    </div>
  </div>
);

export const AdvancedSettingsSection = ({
  value,
  onChange,
  open,
  onToggleOpen,
  disabled = false,
  timezone,
  locale = 'en',
  minValidFromDate,
  minValidUntilDate,
  maxEndDate,
  isEventInThePast = false,
  lockLowStockThreshold = false,
  errors,
  dataTestId = ADVANCED_SETTINGS_SECTION_TEST_ID,
}: AdvancedSettingsSectionProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useWindowSize(SM_VIEWPORT);
  const toggleSize: ToggleSectionSize = isMobileViewport ? TOGGLE_SECTION_SIZE_S : TOGGLE_SECTION_SIZE_L;
  const isReadOnly = disabled || isEventInThePast;
  const isLowStockThresholdReadOnly = isReadOnly || lockLowStockThreshold;

  const { entranceTimeSlot, drinks, lowStockThreshold } = value;

  const updateEntranceTimeSlot = (next: Partial<EntranceTimeSlotValue>) => {
    onChange({ entranceTimeSlot: { ...entranceTimeSlot, ...next } });
  };

  const updateDrinks = (next: Partial<DrinksValue>) => {
    if (!drinks) return;
    onChange({ drinks: { ...drinks, ...next } });
  };

  const updateLowStock = (next: Partial<LowStockThresholdValue>) => {
    if (!lowStockThreshold) return;
    onChange({ lowStockThreshold: { ...lowStockThreshold, ...next } });
  };

  const handleModeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    updateEntranceTimeSlot({ mode: event.currentTarget.value as EntranceTimeSlotMode });
  };

  const handleThresholdChange = (raw: string) => {
    updateLowStock({ quantity: digitsOnly(raw) });
  };

  const handleDrinksChange = (raw: string) => {
    updateDrinks({ quantity: digitsOnly(raw) });
  };

  const previewCount = Number.parseInt(lowStockThreshold?.quantity ?? '', 10);
  const hasValidPreview = !Number.isNaN(previewCount) && previewCount > 0;
  const previewBadgeKey =
    previewCount === 1
      ? 'offers:admissionLowStockThresholdPreviewBadge'
      : 'offers:admissionLowStockThresholdPreviewBadge_plural';
  const previewBadge: ReactNode = hasValidPreview ? (
    <OutlineTag
      size={OUTLINE_TAG_SIZE_XS}
      variant={OUTLINE_TAG_VARIANT_WARNING}
      className='self-start'
      dataTestId={tid(dataTestId, 'low-stock-preview-badge')}>
      {t(previewBadgeKey, { formattedCount: new Intl.NumberFormat(locale).format(previewCount) })}
    </OutlineTag>
  ) : null;

  return (
    <SectionDisclosure
      title={t('offers:admissionAdvancedSettings')}
      open={open}
      onToggleOpen={onToggleOpen}
      dataTestId={`${dataTestId}-disclosure`}>
      <section data-testid={dataTestId} className='flex flex-col gap-6 w-full py-4 md:py-6'>
        <div className='flex flex-col gap-2' data-testid={tid(dataTestId, 'entrance-time-slot')}>
          <LabelInfo
            text={t('offers:admissionAccessValidityTooltip')}
            ariaLabel={t('offers:admissionEntranceTimeSlotLabel')}
            marginBottomClassName=''
            customColors='text-secondary'>
            {t('offers:admissionEntranceTimeSlotLabel')}
          </LabelInfo>
          <Select
            value={entranceTimeSlot.mode}
            onChange={handleModeChange}
            disabled={isReadOnly}
            readOnly={isReadOnly}
            data-testid={tid(dataTestId, 'entrance-time-slot-select')}>
            <SelectOption
              value={ENTRANCE_TIME_SLOT_MODE_EVENT}
              label={t('offers:admissionAccessValidityEventOption')}
            />
            <SelectOption
              value={ENTRANCE_TIME_SLOT_MODE_LIMITED}
              label={t('offers:admissionAccessValidityLimitedOption')}
            />
          </Select>

          {entranceTimeSlot.mode === ENTRANCE_TIME_SLOT_MODE_LIMITED && (
            <>
              <div className='mt-2 grid grid-cols-1 items-start gap-6 md:grid-cols-2'>
                <DateTimeRow
                  prefix={dataTestId}
                  testid='valid-from'
                  label={t('offers:admissionValidityFromDateLabel')}
                  date={entranceTimeSlot.validFrom}
                  time={entranceTimeSlot.validFromTime}
                  onDateChange={(next) => updateEntranceTimeSlot({ validFrom: next })}
                  onTimeChange={(next) => updateEntranceTimeSlot({ validFromTime: next })}
                  minBookingDate={minValidFromDate}
                  maxBookingDate={maxEndDate}
                  timezone={timezone}
                  locale={locale}
                  disabled={isReadOnly}
                  hasDateError={Boolean(errors?.entranceTimeSlotValidFrom)}
                  hasTimeError={Boolean(errors?.entranceTimeSlotValidFromTime)}
                  dateError={errors?.entranceTimeSlotValidFrom}
                  timeError={errors?.entranceTimeSlotValidFromTime}
                />
                <DateTimeRow
                  prefix={dataTestId}
                  testid='valid-until'
                  label={t('offers:admissionValidUntilLabel')}
                  date={entranceTimeSlot.validUntil}
                  time={entranceTimeSlot.validUntilTime}
                  onDateChange={(next) => updateEntranceTimeSlot({ validUntil: next })}
                  onTimeChange={(next) => updateEntranceTimeSlot({ validUntilTime: next })}
                  minBookingDate={minValidUntilDate}
                  maxBookingDate={maxEndDate}
                  timezone={timezone}
                  locale={locale}
                  disabled={isReadOnly}
                  hasDateError={Boolean(errors?.entranceTimeSlotValidUntil)}
                  hasTimeError={Boolean(errors?.entranceTimeSlotValidUntilTime)}
                  dateError={errors?.entranceTimeSlotValidUntil}
                  timeError={errors?.entranceTimeSlotValidUntilTime}
                />
              </div>
              <FieldError
                prefix={dataTestId}
                testid='entrance-time-slot-range'
                message={errors?.entranceTimeSlotRange}
              />
            </>
          )}
        </div>

        <div className='flex flex-col'>
          {lowStockThreshold && (
            <ToggleSection
              value={lowStockThreshold.enabled}
              onChange={(enabled) => updateLowStock({ enabled })}
              label={t('offers:admissionLowStockThresholdToggleLabel')}
              subLabel={t('offers:admissionLowStockThresholdToggleSublabel')}
              badge={TOGGLE_SECTION_BADGE_NEW}
              size={toggleSize}
              disabled={isLowStockThresholdReadOnly}
              dataTestId={tid(dataTestId, 'low-stock')}>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-2'>
                  <span className='text-body-s text-secondary'>{t('offers:admissionLowStockThresholdInputLabel')}</span>
                  <div className='inline-flex items-center gap-2 self-start'>
                    <Input
                      type='number'
                      inputMode='numeric'
                      value={lowStockThreshold.quantity}
                      onChange={(e) => handleThresholdChange(e.currentTarget.value)}
                      hasError={Boolean(errors?.lowStockThresholdQuantity)}
                      disabled={isLowStockThresholdReadOnly}
                      widthClassName='w-[75px]'
                      min={1}
                      step={1}
                      data-testid={tid(dataTestId, 'low-stock-input')}
                    />
                    <span className='text-body-s text-primary'>
                      {t('offers:admissionLowStockThresholdInputTrailing')}
                    </span>
                  </div>
                  <FieldError
                    prefix={dataTestId}
                    testid='low-stock-input'
                    message={errors?.lowStockThresholdQuantity}
                  />
                </div>
                {hasValidPreview && (
                  <div className='flex flex-col gap-2'>
                    <span className='text-body-xs text-secondary'>
                      {t('offers:admissionLowStockThresholdPreviewLabel')}
                    </span>
                    {previewBadge}
                  </div>
                )}
              </div>
            </ToggleSection>
          )}

          {drinks && (
            <ToggleSection
              value={drinks.enabled}
              onChange={(enabled) => updateDrinks({ enabled })}
              label={t('offers:admissionDrinksToggleLabel')}
              subLabel={t('offers:admissionDrinksToggleSublabel')}
              badge={TOGGLE_SECTION_BADGE_NEW}
              size={toggleSize}
              disabled={isReadOnly}
              dataTestId={tid(dataTestId, 'drinks')}>
              <div className='flex flex-col gap-2'>
                <div className='inline-flex items-center gap-2 self-start'>
                  <Input
                    type='number'
                    inputMode='numeric'
                    value={drinks.quantity}
                    onChange={(e) => handleDrinksChange(e.currentTarget.value)}
                    hasError={Boolean(errors?.drinksQuantity)}
                    disabled={isReadOnly}
                    widthClassName='w-[75px]'
                    min={0}
                    step={1}
                    data-testid={tid(dataTestId, 'drinks-input')}
                  />
                  <span className='text-body-s text-primary'>{t('offers:admissionDrinksInputTrailing')}</span>
                </div>
                <FieldError prefix={dataTestId} testid='drinks-input' message={errors?.drinksQuantity} />
              </div>
            </ToggleSection>
          )}
        </div>
      </section>
    </SectionDisclosure>
  );
};
